import { z } from "zod"

import {
  branchIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const boundedText = (maximum: number) => z.string().trim().min(1).max(maximum)
const currency = z.string().regex(/^[A-Z]{3}$/)

export const changeOrderStateV1Schema = z.enum([
  "draft",
  "office_review_required",
  "ready_for_customer",
  "approved",
  "declined",
  "changes_requested",
  "withdrawn",
])

export const changeOrderLineV1Schema = z.object({
  serviceId: entityIdSchema,
  pricingSnapshotId: entityIdSchema,
  quantityMillis: z.number().int().positive(),
  unitAmountMinor: z.number().int().nonnegative(),
  subtotalMinor: z.number().int(),
  customPrice: z.boolean(),
  discountAmountMinor: z.number().int().nonnegative(),
}).strict()

const commercialRevision = z.object({
  revisionId: entityIdSchema,
  scopeDelta: boundedText(10_000),
  lineItems: z.array(changeOrderLineV1Schema).min(1).max(100),
  incrementalSubtotalMinor: z.number().int(),
  incrementalTaxMinor: z.number().int(),
  incrementalTotalMinor: z.number().int(),
  scheduleImpactMinutes: z.number().int().min(-10_080).max(10_080),
  scheduleImpactNote: boundedText(2_000),
  depositRequirementMinor: z.number().int().nonnegative(),
  taxSnapshotId: entityIdSchema,
  paymentPolicyDecisionId: entityIdSchema,
  officeReviewReasons: z.array(z.enum([
    "custom_price",
    "discount",
    "amount_limit",
  ])).max(3),
}).strict()

export const changeOrderCreateV1Schema = commercialRevision.extend({
  changeOrderId: entityIdSchema,
  fieldProposalId: entityIdSchema.nullable().optional(),
  serviceOrderId: entityIdSchema,
  originatingVisitId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  customerId: entityIdSchema,
  serviceLocationId: entityIdSchema,
  currency,
}).strict()

export const changeOrderReviseV1Schema = commercialRevision.extend({
  previousRevisionId: entityIdSchema,
  serviceOrderExpectedVersion: z.number().int().positive(),
  revisionReason: boundedText(1_000),
}).strict()

export const changeOrderPresentV1Schema = z.object({
  revisionId: entityIdSchema,
  officeApprovalReason: boundedText(1_000).nullable(),
  customerContactId: entityIdSchema,
  secureLinkId: entityIdSchema,
  secureLinkTokenHash: z.string().regex(/^[a-f0-9]{64}$/),
  actionTokenId: entityIdSchema,
  actionTokenHash: z.string().regex(/^[a-f0-9]{64}$/),
  actionTokenExpiresAt: z.string().datetime({ offset: false }),
  permittedAction: z.enum(["approve", "decline", "request_changes"]),
}).strict()

export const changeOrderCustomerActionV1Schema = z.object({
  revisionId: entityIdSchema,
  decisionId: entityIdSchema,
  contactId: entityIdSchema,
  actionTokenHash: z.string().regex(/^[a-f0-9]{64}$/),
  action: z.enum(["approve", "decline", "request_changes"]),
  typedName: z.string().trim().max(200),
  termsAccepted: z.boolean(),
  comments: z.string().trim().max(2_000).nullable(),
  occurredAt: z.string().datetime({ offset: false }),
  depositReceiptId: entityIdSchema.nullable(),
}).strict()

export type ChangeOrderCreateV1 = z.infer<typeof changeOrderCreateV1Schema>
export type ChangeOrderReviseV1 = z.infer<typeof changeOrderReviseV1Schema>
export type ChangeOrderPresentV1 = z.infer<typeof changeOrderPresentV1Schema>
export type ChangeOrderCustomerActionV1 = z.infer<
  typeof changeOrderCustomerActionV1Schema
>
